// part-menu.js
//
// Progressive enhancement for the grouped pickers of the ticket forms: the wall
// of checkboxes Django renders becomes a menu bar. A button opens the list of
// groups, a group opens its parts in a second panel beside the first, the way
// the menu of a desktop application does.
//
// The boxes themselves stay in the DOM and keep the value, so the form posts
// the same names, Django validates the same field, and a page without
// JavaScript still shows the plain grouped list -- only the presentation is
// replaced. What is ticked is repeated as chips under the button, because a
// closed menu that hides its own selection is worse than the wall it replaced.
//
// Bound to `.js-cascade-menu`, which CascadeMenuList in apps/service/forms.py
// puts on the widget. The hierarchy it reads is the markup Django writes for a
// grouped CheckboxSelectMultiple, see django/forms/widgets/multiple_input.html:
//
//   <div id="id_suspected_components" class="checkbox-list js-cascade-menu">
//     <div><label>Electronics</label>
//       <div><label for="id_..._0_0"><input type="checkbox" ...> BCU</label></div>
//     </div>
//   </div>
//
// so a group is a child <div>, its name the <label> that carries no `for`, and
// its parts the boxes inside it. GroupedChoiceIterator in forms.py is what puts
// the choices into groups in the first place.

const SELECTOR = '.js-cascade-menu';

// Long enough that crossing a row on the way to the open submenu does not tear
// it away, short enough that aiming at a group still feels immediate.
const HOVER_DELAY = 140;

const BUTTON_CLASS =
  'flex w-full items-center justify-between gap-3 rounded-lg border border-gray-300 bg-gray-50 ' +
  'px-3 py-2.5 text-left text-sm text-gray-900 focus:border-blue-500 focus:outline-none ' +
  'focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

const PANEL_CLASS =
  'absolute z-50 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 ' +
  'shadow-lg dark:border-gray-700 dark:bg-gray-800';

const ROW_CLASS =
  'flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm text-gray-700 ' +
  'hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:text-gray-200 ' +
  'dark:hover:bg-gray-700 dark:focus:bg-gray-700';

const OPTION_CLASS =
  'flex cursor-pointer items-center gap-2 whitespace-nowrap px-3 py-1.5 text-sm text-gray-700 ' +
  'hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700';

// The same look a box has in a plain list, see CHECKBOX_CLASSES in forms.py.
const BOX_CLASS =
  'h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 ' +
  'dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-blue-600';

const CHIP_CLASS =
  'inline-flex items-center gap-1 rounded-full bg-blue-50 py-0.5 pl-2 pr-1 text-xs font-medium ' +
  'text-blue-700 dark:bg-gray-700 dark:text-blue-300';

const CHIP_REMOVE_CLASS =
  'rounded-full p-0.5 text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-1 ' +
  'focus:ring-blue-500 dark:text-blue-300 dark:hover:bg-gray-600';

const COUNT_CLASS = 'ml-auto text-xs text-gray-400 dark:text-gray-500';

let uid = 0;

/** The label of one box: the text of the <label> Django wrapped around it. */
function partLabel(box) {
  const label = box.closest('label');
  return (label ? label.textContent : box.value).trim();
}

/**
 * The groups of a rendered checkbox list, in the order they stand in.
 *
 * A choice outside any group -- which the widget renders as a bare <div> with a
 * labelled box in it -- is collected into one group at the end, so an ungrouped
 * queryset still opens a menu rather than an empty one.
 */
function readGroups(list) {
  const groups = [];
  const loose = [];

  for (const node of list.children) {
    const boxes = [...node.querySelectorAll('input[type="checkbox"]')];
    if (!boxes.length) continue;
    const heading = node.querySelector(':scope > label:not([for])');
    if (heading) {
      groups.push({ name: heading.textContent.trim(), boxes });
    } else {
      loose.push(...boxes);
    }
  }
  if (loose.length) groups.push({ name: 'Ungrouped', boxes: loose });
  return groups;
}

function icon(path, className) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', className);
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  const node = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  node.setAttribute('fill-rule', 'evenodd');
  node.setAttribute('d', path);
  node.setAttribute('clip-rule', 'evenodd');
  svg.appendChild(node);
  return svg;
}

const CARET =
  'M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 ' +
  '01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z';
const CHEVRON =
  'M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 ' +
  '010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z';
const CROSS =
  'M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 ' +
  '1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 ' +
  '010-1.414z';

class PartMenu {
  constructor(list) {
    this.list = list;
    this.id = list.id || `part-menu-${++uid}`;
    this.placeholder = list.dataset.placeholder || 'Nothing selected';
    this.groups = readGroups(list);
    this.open = false;
    this.groupIndex = -1;
    this.proxies = [];
    this.hoverTimer = null;

    this.build();
    this.bind();
    this.sync();
  }

  /* ---------------------------------------------------------------- build */

  build() {
    const list = this.list;

    this.root = document.createElement('div');
    this.root.className = 'relative part-menu';
    list.parentNode.insertBefore(this.root, list);

    // The boxes stay in the form -- they are what is posted -- but they are out
    // of the page and out of the tab order while the menu stands in for them.
    // `hidden` alone loses against the grid rule on .part-picker .checkbox-list,
    // which is why _ticket_form_styles.html spells the hidden case out.
    list.hidden = true;

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = BUTTON_CLASS;
    this.button.id = `${this.id}-button`;
    this.button.setAttribute('aria-haspopup', 'true');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', `${this.id}-menu`);

    this.summary = document.createElement('span');
    this.summary.className = 'truncate';
    this.button.appendChild(this.summary);
    this.button.appendChild(icon(CARET, 'h-4 w-4 shrink-0 text-gray-400'));
    this.root.appendChild(this.button);

    this.menu = document.createElement('div');
    this.menu.id = `${this.id}-menu`;
    this.menu.className = `${PANEL_CLASS} hidden`;
    this.menu.style.minWidth = '13rem';
    this.menu.setAttribute('role', 'group');
    this.menu.setAttribute('aria-labelledby', this.button.id);

    this.rows = this.groups.map((group, index) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = ROW_CLASS;
      row.setAttribute('aria-expanded', 'false');
      row.setAttribute('aria-controls', `${this.id}-submenu`);

      const name = document.createElement('span');
      name.className = 'truncate';
      name.textContent = group.name;
      row.appendChild(name);

      // How many of the group are ticked, so a group with something in it is
      // known without opening it.
      group.count = document.createElement('span');
      group.count.className = COUNT_CLASS;
      row.appendChild(group.count);
      row.appendChild(icon(CHEVRON, 'h-4 w-4 shrink-0 text-gray-400'));

      row.addEventListener('click', () => this.openGroup(index));
      row.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.hoverTimer = window.setTimeout(() => this.openGroup(index), HOVER_DELAY);
      });
      row.addEventListener('pointerleave', () => this.clearHover());
      row.addEventListener('focus', () => this.clearHover());

      this.menu.appendChild(row);
      return row;
    });
    this.root.appendChild(this.menu);

    // One submenu, refilled on every group: seven panels for seven groups would
    // be seven times the markup for the one panel that is ever visible.
    this.submenu = document.createElement('div');
    this.submenu.id = `${this.id}-submenu`;
    this.submenu.className = `${PANEL_CLASS} hidden`;
    this.submenu.setAttribute('role', 'group');
    this.root.appendChild(this.submenu);

    this.chips = document.createElement('div');
    this.chips.className = 'mt-2 flex flex-wrap gap-1';
    this.root.appendChild(this.chips);

    this.root.appendChild(list);
  }

  /* ----------------------------------------------------------------- bind */

  bind() {
    this.button.addEventListener('click', () => this.toggle());
    this.button.addEventListener('keydown', (e) => this.onButtonKeydown(e));
    this.menu.addEventListener('keydown', (e) => this.onMenuKeydown(e));
    this.submenu.addEventListener('keydown', (e) => this.onSubmenuKeydown(e));
    // Moving onto the open submenu must not let the group underneath switch.
    this.submenu.addEventListener('pointerenter', () => this.clearHover());

    this.onDocumentPointerDown = (e) => {
      if (this.open && !this.root.contains(e.target)) this.close();
    };
    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    // Tabbing out of the menu closes it, the same way clicking beside it does.
    this.root.addEventListener('focusout', (e) => {
      if (this.open && !this.root.contains(e.relatedTarget)) this.close();
    });

    // Something else may tick a box -- the chips do, and so could a script on
    // the page. The menu follows the boxes, never the other way round.
    this.list.addEventListener('change', () => this.sync());
  }

  /* ---------------------------------------------------------------- state */

  boxes() {
    return this.groups.flatMap((group) => group.boxes);
  }

  setChecked(box, checked) {
    box.checked = checked;
    // Bubbles to the list, where the listener above picks it up, and out of it
    // for anything else that watches the field.
    box.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Redraw everything that says what is ticked, from the boxes themselves. */
  sync() {
    const picked = this.boxes().filter((box) => box.checked);

    if (picked.length === 0) {
      this.summary.textContent = this.placeholder;
    } else if (picked.length === 1) {
      this.summary.textContent = partLabel(picked[0]);
    } else {
      this.summary.textContent = `${picked.length} selected`;
    }
    this.summary.classList.toggle('text-gray-400', picked.length === 0);
    this.summary.classList.toggle('dark:text-gray-400', picked.length === 0);

    this.groups.forEach((group) => {
      const count = group.boxes.filter((box) => box.checked).length;
      group.count.textContent = count ? String(count) : '';
    });

    if (this.groupIndex >= 0) {
      this.groups[this.groupIndex].boxes.forEach((box, i) => {
        if (this.proxies[i]) this.proxies[i].checked = box.checked;
      });
    }

    this.renderChips(picked);
  }

  /** What is ticked, under the button: a closed menu still names its parts. */
  renderChips(picked) {
    this.chips.textContent = '';
    picked.forEach((box) => {
      const chip = document.createElement('span');
      chip.className = CHIP_CLASS;
      chip.appendChild(document.createTextNode(partLabel(box)));

      if (!box.disabled) {
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = CHIP_REMOVE_CLASS;
        remove.setAttribute('aria-label', `Remove ${partLabel(box)}`);
        remove.appendChild(icon(CROSS, 'h-3 w-3'));
        remove.addEventListener('click', () => this.setChecked(box, false));
        chip.appendChild(remove);
      }
      this.chips.appendChild(chip);
    });
  }

  /* ----------------------------------------------------------- open/close */

  toggle() {
    if (this.open) {
      this.close();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    if (this.open) return;
    this.open = true;
    this.menu.classList.remove('hidden');
    this.button.setAttribute('aria-expanded', 'true');
    this.positionMenu();
  }

  close() {
    if (!this.open) return;
    this.open = false;
    this.clearHover();
    this.closeGroup();
    this.menu.classList.add('hidden');
    this.button.setAttribute('aria-expanded', 'false');
  }

  clearHover() {
    window.clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  }

  /** Show the parts of one group beside the group list. */
  openGroup(index) {
    this.clearHover();
    if (!this.open) this.openMenu();
    if (index === this.groupIndex) return;

    this.groupIndex = index;
    this.rows.forEach((row, i) => {
      const active = i === index;
      row.setAttribute('aria-expanded', active ? 'true' : 'false');
      row.classList.toggle('bg-gray-100', active);
      row.classList.toggle('dark:bg-gray-700', active);
    });

    this.renderOptions(this.groups[index]);
    this.submenu.classList.remove('hidden');
    this.positionSubmenu(this.rows[index]);
  }

  closeGroup() {
    this.groupIndex = -1;
    this.proxies = [];
    this.submenu.classList.add('hidden');
    this.rows.forEach((row) => {
      row.setAttribute('aria-expanded', 'false');
      row.classList.remove('bg-gray-100', 'dark:bg-gray-700');
    });
  }

  /**
   * The parts of one group, as boxes that stand in for the hidden ones.
   *
   * A proxy rather than the box itself: the boxes are the form, and moving them
   * into a panel that is emptied on every group change would take the field
   * apart. Ticking a proxy ticks the box it belongs to and nothing else.
   */
  renderOptions(group) {
    this.submenu.textContent = '';
    this.submenu.setAttribute('aria-label', group.name);

    this.proxies = group.boxes.map((box) => {
      const option = document.createElement('label');
      option.className = OPTION_CLASS;

      const proxy = document.createElement('input');
      proxy.type = 'checkbox';
      proxy.className = BOX_CLASS;
      proxy.checked = box.checked;
      proxy.disabled = box.disabled;
      // The menu stays open: a part picker is a multiple choice, and closing on
      // every tick would mean opening it again for the second part.
      proxy.addEventListener('change', () => this.setChecked(box, proxy.checked));

      option.appendChild(proxy);
      option.appendChild(document.createTextNode(partLabel(box)));
      this.submenu.appendChild(option);
      return proxy;
    });
  }

  /* ------------------------------------------------------------ placement */

  /** Drop below the button, flip above when there is not enough room. */
  positionMenu() {
    this.menu.classList.remove('bottom-full', 'mb-1', 'top-full', 'mt-1');
    this.menu.style.left = '0px';
    const space = window.innerHeight - this.button.getBoundingClientRect().bottom;
    const flip = space < this.menu.offsetHeight + 16;
    this.menu.classList.add(...(flip ? ['bottom-full', 'mb-1'] : ['top-full', 'mt-1']));
  }

  /**
   * Beside the group list, level with the row it belongs to.
   *
   * The submenu is a sibling of the group panel rather than a child of it: the
   * panel scrolls and clips, and a child would be cut off at its edge instead
   * of standing next to it. Both are placed against the root, which is the only
   * positioned element here.
   */
  positionSubmenu(row) {
    const root = this.root.getBoundingClientRect();
    const menu = this.menu.getBoundingClientRect();
    const rect = row.getBoundingClientRect();

    // Level with the row, less the padding the panel carries at its top.
    this.submenu.style.top = `${rect.top - root.top - 4}px`;

    const width = this.submenu.offsetWidth;
    const flip = menu.right + width + 8 > window.innerWidth;
    const left = flip ? menu.left - root.left - width : menu.right - root.left;
    this.submenu.style.left = `${left}px`;

    // A group opened near the bottom of the window would run off the page.
    const overflow = this.submenu.getBoundingClientRect().bottom - window.innerHeight + 8;
    if (overflow > 0) {
      this.submenu.style.top = `${parseFloat(this.submenu.style.top) - overflow}px`;
    }
  }

  /* ------------------------------------------------------------- keyboard */

  onButtonKeydown(e) {
    if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) return;
    e.preventDefault();
    this.openMenu();
    this.focusRow(e.key === 'ArrowUp' ? this.rows.length - 1 : 0);
  }

  focusRow(index) {
    const row = this.rows[Math.max(0, Math.min(this.rows.length - 1, index))];
    if (row) row.focus();
  }

  onMenuKeydown(e) {
    const index = this.rows.indexOf(document.activeElement);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.focusRow(index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.focusRow(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        this.focusRow(0);
        break;
      case 'End':
        e.preventDefault();
        this.focusRow(this.rows.length - 1);
        break;
      case 'ArrowRight':
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (index < 0) break;
        this.openGroup(index);
        if (this.proxies[0]) this.proxies[0].focus();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.closeGroup();
        break;
      case 'Escape':
        e.preventDefault();
        this.close();
        this.button.focus();
        break;
      default:
        break;
    }
  }

  onSubmenuKeydown(e) {
    const index = this.proxies.indexOf(document.activeElement);
    const focus = (i) => {
      const proxy = this.proxies[Math.max(0, Math.min(this.proxies.length - 1, i))];
      if (proxy) proxy.focus();
    };
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focus(index + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focus(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focus(0);
        break;
      case 'End':
        e.preventDefault();
        focus(this.proxies.length - 1);
        break;
      case 'ArrowLeft':
      case 'Escape': {
        e.preventDefault();
        const row = this.rows[this.groupIndex];
        this.closeGroup();
        if (row) row.focus();
        break;
      }
      default:
        break;
    }
  }

  /* ------------------------------------------------------------- teardown */

  destroy() {
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    this.list.hidden = false;
    this.root.parentNode.insertBefore(this.list, this.root);
    this.root.remove();
    delete this.list._partMenu;
  }
}

/** Enhance every not-yet-enhanced .js-cascade-menu below `root`. */
export function initPartMenus(root = document) {
  const lists =
    root instanceof Element && root.matches(SELECTOR)
      ? [root]
      : [...root.querySelectorAll(SELECTOR)];

  lists.forEach((list) => {
    if (list._partMenu) return;
    // A field whose catalog is empty has nothing to open. Left as it is, the
    // page shows the empty list rather than a button onto an empty panel.
    if (!readGroups(list).length) return;
    list._partMenu = new PartMenu(list);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPartMenus());
} else {
  initPartMenus();
}

// A form section swapped in by HTMX carries the plain list, so enhance it too.
// The event bubbles, so listening on document also covers late partials.
document.addEventListener('htmx:afterSwap', (e) => initPartMenus(e.target));

window.PartMenu = { init: initPartMenus };

export default PartMenu;
